import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  /** @type {Array<{discount_code: string, product_variant: string, use_code_prefix?: boolean, minimum_amount?: string, gift_minimum_amount?: string, first_time_buyer_only?: boolean | string, add_automatically?: boolean | string}>} */
  #gwpDiscountMappings = [];

  /** @type {MutationObserver | null} */
  #gwpDatasetObserver = null;
  
  connectedCallback() {
    super.connectedCallback();

    this.#hydrateGwpDiscountMappings();
    this.#observeGwpDiscountDataset();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    
    this.#updateCartAttributes();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    
    this.#gwpDatasetObserver?.disconnect();
    this.#gwpDatasetObserver = null;

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    // If the removed line has a gift voucher code and the mapping is not manual,
    // remove the linked discount code as well.
    const giftVoucherCode = cartItemRowToRemove.dataset.giftVoucherCode?.trim();
    if (giftVoucherCode) {
      const mapping = this.#getGwpMappingForDiscountCode(giftVoucherCode.toLowerCase());
      const shouldRemoveCode = this.#isTruthy(mapping?.add_automatically);

      if (shouldRemoveCode) {
        const selector = `cart-discount-component .cart-discount__pill[data-discount-code="${CSS.escape(giftVoucherCode)}"] button.cart-discount__pill-remove`;
        const discountPill = /** @type {HTMLButtonElement | null} */ (
          this.querySelector(selector)
        );
        discountPill?.click();
      }
    }

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {string} config.key - The key.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);
        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const settingsElement = document.querySelector('[data-cart-progress-settings]');
        /** @type {{ freeProductThreshold?: number }} */
        const progressSettings = settingsElement ? JSON.parse(settingsElement.textContent || '{}') : {};
        const freeProductThreshold = Number(progressSettings.freeProductThreshold || 0);

        const items = Array.isArray(parsedResponseText.items) ? parsedResponseText.items : [];
        const thresholdGiftItem = items.find((item) => {
          const props = item?.properties || {};
          return props._threshold_gift === 'true' || props._threshold_gift === true;
        });

        // Gifts linked to a parent line (`_gift_parent_key`) are handled by #getLinkedGiftUpdates.
        const metafieldGiftProduct = items.find((item) => {
          const props = item?.properties || {};
          return (props._metafield_gift === 'true' || props._metafield_gift === true) && !props._gift_parent_key;
        });

        const metafieldGiftProductParent = items.find((item) => {
          const props = item?.properties || {};
          return props._metafield_gift_parent === 'true' || props._metafield_gift_parent === true;
        });

        const freeGiftItems = items.filter((/** @type {Record<string, any>} */ item) => {
          const props = item?.properties || {};
          return props._free_gift === 'true' || props._free_gift === true;
        });

        const totalPrice = typeof parsedResponseText.total_price === 'number' ? parsedResponseText.total_price : null;
        const discountCodes = Array.isArray(parsedResponseText.discount_codes) ? parsedResponseText.discount_codes : [];
        const cartTotal = totalPrice !== null ? totalPrice / 100 : null;
        const isFirstTimeCustomer = this.#isFirstTimeCustomer();
        const voucherCodeGiftsBelowThreshold = this.#findVoucherCodeGiftsBelowThreshold(
          items,
          discountCodes,
          cartTotal,
          isFirstTimeCustomer
        );

        // Collect every gift line that no longer qualifies, so all of them are
        // removed with a single cart update call instead of one call per gift.
        /** @type {Map<string, string>} */
        const giftsToRemove = new Map();

        /**
         * Queues a gift line for removal in the batched cart update.
         * @param {Record<string, any> | undefined} gift - The cart line.
         * @param {string} reason - Why the line is being removed, used for error logging.
         */
        const queueGiftRemoval = (gift, reason) => {
          const key = gift?.key;
          if (typeof key !== 'string' || key === '' || giftsToRemove.has(key)) return;
          giftsToRemove.set(key, reason);
        };
        if (freeGiftItems.length > 0 && totalPrice !== null && totalPrice <= 0) {
          freeGiftItems.forEach((/** @type {Record<string, any>} */ gift) => {
            queueGiftRemoval(gift, 'free gift');
          });
        }

        if (thresholdGiftItem && freeProductThreshold > 0 && totalPrice !== null && totalPrice < freeProductThreshold) {
          queueGiftRemoval(thresholdGiftItem, 'threshold gift');
        }

        if (metafieldGiftProduct && !metafieldGiftProductParent) {
          queueGiftRemoval(metafieldGiftProduct, 'metafield gift');
        }

        voucherCodeGiftsBelowThreshold.forEach((/** @type {Record<string, any>} */ gift) => {
          queueGiftRemoval(gift, 'voucher code gift');
        });

        // Gifts linked to a parent line follow the parent's quantity (or are removed with it).
        const linkedGiftUpdates = this.#getLinkedGiftUpdates(items);

        if (giftsToRemove.size > 0 || Object.keys(linkedGiftUpdates).length > 0) {
          /** @type {Record<string, number>} */
          const updates = { ...linkedGiftUpdates };
          giftsToRemove.forEach((/** @type {string} */ _reason, /** @type {string} */ key) => {
            updates[key] = 0;
          });

          const reasons = Array.from(new Set(giftsToRemove.values())).join(', ') || 'linked gift sync';

          const updateBody = JSON.stringify({
            updates,
            sections: Array.from(sectionsToUpdate).join(','),
            sections_url: window.location.pathname,
          });

          fetch(`${Theme.routes.cart_update_url}`, fetchConfig('json', { body: updateBody }))
            .then((response) => response.json())
            .then((data) => {
              if (data.errors) {
                console.error(`Failed removing gifts (${reasons})`, data.errors);
              }
              if (data.sections) {
                this.#applySectionsUpdate(data.sections);
              }
            })
            .catch((err) => console.error(`Failed removing gifts (${reasons})`, err));

          return;
        }

        this.#applySectionsUpdate(parsedResponseText.sections);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const sectionsToUpdate = new Set([this.sectionId]);
      cartItemsComponents.forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionsToUpdate.add(item.dataset.sectionId);
        }
      });

      const resource = /** @type {{
        items?: Array<Record<string, any>>,
        discount_codes?: Array<{ applicable?: boolean, code?: string }>
      } | undefined} */ (event.detail.resource);
      const items = Array.isArray(resource?.items) ? resource.items : [];
      const discountCodes = Array.isArray(resource?.discount_codes) ? resource.discount_codes : [];
      const isFirstTimeCustomer = this.#isFirstTimeCustomer();

      const voucherCodeGift = items.filter((/** @type {Record<string, any>} */ item) => {
        const giftVoucherCode = this.#getVoucherCodeFromItem(item);
        const hasGiftVoucherCode = giftVoucherCode !== '';
        const gwpMapping = this.#getGwpMappingForItem(item, giftVoucherCode);
        const firstTimeOnly = String(
          gwpMapping?.first_time_buyer_only ?? ''
        ).toLowerCase() === 'true';
        const notEligibleForBuyer = firstTimeOnly && !isFirstTimeCustomer;
        const noMatchingCode = !discountCodes.some(
          (/** @type {{ applicable?: boolean, code?: string }} */ discountCode) =>
            discountCode?.applicable === true
            && typeof discountCode.code === 'string'
            && discountCode.code.trim().toLowerCase() === giftVoucherCode.toLowerCase()
        );
        return hasGiftVoucherCode && (noMatchingCode || notEligibleForBuyer);
      });

      if (voucherCodeGift.length > 0) {
        const removeRequests = voucherCodeGift.map((/** @type {Record<string, any>} */ gift) => {
          const bodyRemove = JSON.stringify({
            id: gift.key,
            quantity: 0,
            sections: Array.from(sectionsToUpdate).join(','),
            sections_url: window.location.pathname,
          });

          return fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body: bodyRemove }));
        });

        Promise.allSettled(removeRequests)
          .then((results) => {
            const failedRemovals = results.filter(
              (/** @type {PromiseSettledResult<Response>} */ result) => result.status === 'rejected'
            );
            if (failedRemovals.length > 0) {
              console.error('Failed removing one or more voucher code gifts', failedRemovals);
            }
          })
          .finally(() => {
            sectionRenderer.renderSection(this.sectionId, { cache: false });
          });

        return;
      }

      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }

    // A parent may have been added again from outside the cart (PDP, quick add),
    // so its linked gift has to follow the new quantity.
    this.#syncLinkedGifts();
  };

  /** Shared across cart items components (drawer + cart page) to avoid duplicate syncs. */
  static #linkedGiftSyncInFlight = false;

  /**
   * Calculates the quantity updates for gifts linked to a parent line via `_gift_parent_key`.
   * A linked gift always has the quantity of its parent line and is removed when the parent is gone.
   * @param {Array<Record<string, any>>} items - The cart items.
   * @returns {Record<string, number>} Updates keyed by the gift's line key.
   */
  #getLinkedGiftUpdates(items) {
    /** @type {Map<string, number>} */
    const parentQuantities = new Map();
    items.forEach((item) => {
      if (typeof item?.key === 'string') parentQuantities.set(item.key, Number(item.quantity) || 0);
    });

    /** @type {Record<string, number>} */
    const updates = {};
    items.forEach((item) => {
      const parentKey = item?.properties?._gift_parent_key;
      if (typeof parentKey !== 'string' || parentKey === '' || typeof item.key !== 'string') return;

      const targetQuantity = parentQuantities.get(parentKey) ?? 0;
      if (Number(item.quantity) !== targetQuantity) updates[item.key] = targetQuantity;
    });

    return updates;
  }

  /**
   * Reads the current cart and aligns linked gift quantities with their parents.
   */
  async #syncLinkedGifts() {
    if (CartItemsComponent.#linkedGiftSyncInFlight) return;
    CartItemsComponent.#linkedGiftSyncInFlight = true;

    try {
      const cart = await fetch(`${Theme.routes.cart_url}.js`).then((response) => response.json());
      const items = Array.isArray(cart?.items) ? cart.items : [];
      const updates = this.#getLinkedGiftUpdates(items);
      if (Object.keys(updates).length === 0) return;

      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const sectionsToUpdate = new Set([this.sectionId]);
      cartItemsComponents.forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionsToUpdate.add(item.dataset.sectionId);
        }
      });

      const body = JSON.stringify({
        updates,
        sections: Array.from(sectionsToUpdate).join(','),
        sections_url: window.location.pathname,
      });

      const data = await fetch(`${Theme.routes.cart_update_url}`, fetchConfig('json', { body })).then((response) =>
        response.json()
      );

      if (data.errors) {
        console.error('Failed syncing linked gifts', data.errors);
        return;
      }
      if (data.sections) this.#applySectionsUpdate(data.sections);
    } catch (error) {
      console.error('Failed syncing linked gifts', error);
    } finally {
      CartItemsComponent.#linkedGiftSyncInFlight = false;
    }
  }

  /**
   * Gets voucher code from cart line properties.
   * @param {Record<string, any>} item
   * @returns {string}
   */
  #getVoucherCodeFromItem(item) {
    const props = item?.properties || {};
    return typeof props._gift_voucher_code === 'string' ? props._gift_voucher_code.trim() : '';
  }

  /**
   * Finds voucher-based gifts that should be removed because buyer/code/threshold rules no longer match.
   * @param {Array<Record<string, any>>} items
   * @param {Array<{ applicable?: boolean, code?: string }>} discountCodes
   * @param {number | null} cartTotal
   * @param {boolean} isFirstTimeCustomer
   * @returns {Array<Record<string, any>>}
   */
  #findVoucherCodeGiftsBelowThreshold(items, discountCodes, cartTotal, isFirstTimeCustomer) {
    return items.filter((/** @type {Record<string, any>} */ item) => {
      const giftVoucherCode = this.#getVoucherCodeFromItem(item);
      if (!giftVoucherCode) return false;

      const gwpMapping = this.#getGwpMappingForItem(item, giftVoucherCode);
      if (!gwpMapping) return false;

      const firstTimeOnly = this.#isTruthy(gwpMapping.first_time_buyer_only);
      if (firstTimeOnly && !isFirstTimeCustomer) return true;

      const hasMatchingApplicableCode = discountCodes.some(
        (/** @type {{ applicable?: boolean, code?: string }} */ discountCode) => {
          if (discountCode?.applicable !== true || typeof discountCode.code !== 'string') return false;
          return this.#matchesDiscountCode(discountCode.code.trim().toLowerCase(), gwpMapping);
        }
      );
      if (!hasMatchingApplicableCode) return true;

      if (cartTotal === null) return false;

      const minimumThreshold = this.#toThreshold(gwpMapping.minimum_amount, 0);
      const goodieThreshold = this.#toThreshold(
        gwpMapping.gift_minimum_amount ?? gwpMapping.minimum_amount,
        minimumThreshold
      );

      return cartTotal < minimumThreshold || cartTotal < goodieThreshold;
    });
  }

  /**
   * Returns whether current buyer is first-time based on data attribute.
   * Missing value defaults to true (guest behavior parity with extension logic).
   * @returns {boolean}
   */
  #isFirstTimeCustomer() {
    const value = this.dataset.firstTimeCustomer;
    return value?.toLowerCase() == 'true';
  }

  /**
   * Finds the matching GWP mapping for a gift line.
   * @param {Record<string, any>} item
   * @param {string} giftVoucherCode
  * @returns {{discount_code?: string, use_code_prefix?: boolean, product_variant?: string, minimum_amount?: string, gift_minimum_amount?: string, first_time_buyer_only?: boolean | string, add_automatically?: boolean | string} | undefined}
   */
  #getGwpMappingForItem(item, giftVoucherCode) {
    if (giftVoucherCode === '') return undefined;

    const lineVariantId = this.#normalizeVariantId(item?.variant_id || item?.variantId || item?.id);
    const normalizedGiftCode = giftVoucherCode.toLowerCase();

    return this.#gwpDiscountMappings.find((mapping) => {
      const mappingVariantId = this.#normalizeVariantId(mapping?.product_variant);
      if (lineVariantId !== '' && mappingVariantId !== '' && mappingVariantId !== lineVariantId) return false;

      const mappingCode = typeof mapping?.discount_code === 'string' ? mapping.discount_code.trim().toLowerCase() : '';
      if (mappingCode === '') return false;
      if (normalizedGiftCode === mappingCode) return true;

      return this.#isTruthy(mapping?.use_code_prefix) && normalizedGiftCode.startsWith(mappingCode);
    });
  }

  /**
   * @param {string} discountCode
  * @returns {{discount_code?: string, use_code_prefix?: boolean, minimum_amount?: string, gift_minimum_amount?: string, first_time_buyer_only?: boolean | string, add_automatically?: boolean | string} | undefined}
   */
  #getGwpMappingForDiscountCode(discountCode) {
    if (discountCode === '') return undefined;

    return this.#gwpDiscountMappings.find((mapping) => this.#matchesDiscountCode(discountCode, mapping));
  }

  /**
   * @param {string} inputCode
   * @param {{discount_code?: string, use_code_prefix?: boolean}} mapping
   * @returns {boolean}
   */
  #matchesDiscountCode(inputCode, mapping) {
    const normalizedInputCode = inputCode.trim().toLowerCase();
    const mappingCode = typeof mapping?.discount_code === 'string' ? mapping.discount_code.trim().toLowerCase() : '';

    if (mappingCode === '') return false;
    if (normalizedInputCode === mappingCode) return true;

    return this.#isTruthy(mapping?.use_code_prefix) && normalizedInputCode.startsWith(mappingCode);
  }

  /**
   * @param {unknown} value
   * @returns {boolean}
   */
  #isTruthy(value) {
    return String(value ?? '').trim().toLowerCase() === 'true';
  }

  /**
   * @param {unknown} value
   * @param {number} fallback
   * @returns {number}
   */
  #toThreshold(value, fallback = 0) {
    const parsed = parseFloat(String(value ?? ''));
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  /**
   * Normalizes variant IDs from numeric or gid format to comparable numeric string.
   * @param {unknown} value
   * @returns {string}
   */
  #normalizeVariantId(value) {
    if (value == null) return '';
    const str = String(value).trim();
    if (str === '') return '';
    return str.replace('gid://shopify/ProductVariant/', '');
  }

  #hydrateGwpDiscountMappings() {
    const raw = this.dataset.gwpDiscounts;
    if (!raw) {
      this.#gwpDiscountMappings = [];
      return;
    }

    try {
      const parsed = JSON.parse(raw);
      this.#gwpDiscountMappings = Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      this.#gwpDiscountMappings = [];
    }

    this.removeAttribute('data-gwp-discounts');
  }

  /**
   * Ensures re-rendered markup cannot leave raw GWP mapping data on the element.
   */
  #observeGwpDiscountDataset() {
    this.#gwpDatasetObserver?.disconnect();

    this.#gwpDatasetObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type !== 'attributes') continue;
        if (mutation.attributeName !== 'data-gwp-discounts') continue;
        if (!this.hasAttribute('data-gwp-discounts')) continue;

        this.#hydrateGwpDiscountMappings();
      }
    });

    this.#gwpDatasetObserver.observe(this, {
      attributes: true,
      attributeFilter: ['data-gwp-discounts'],
    });
  }

  /**
   * Applies a cart sections response: dispatches a cart update event and morphs the section.
   * @param {Record<string, string>} sections - The rendered sections keyed by section id.
   */
  #applySectionsUpdate(sections) {
    const sectionHTML = sections[this.sectionId] ?? '';
    const newSectionHTML = new DOMParser().parseFromString(sectionHTML, 'text/html');

    // Grab the new cart item count from a hidden element
    const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
    const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

    this.dispatchEvent(
      new CartUpdateEvent({}, this.sectionId, {
        itemCount: newCartItemCount,
        source: 'cart-items-component',
        sections,
      })
    );

    morphSection(this.sectionId, sectionHTML);
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  async #updateCartAttributes() {
    const settingsElement = document.querySelector('[data-cart-progress-settings]');
    if (!settingsElement) return;

    const settings = JSON.parse(settingsElement.textContent);
    const attributes = {};

    if (settings.showFreeProductThreshold && settings.freeProductThresholdFormatted) {
      attributes['_gift_threshold'] = settings.freeProductThresholdFormatted.toString();
    } else {
      attributes['_gift_threshold'] = null;
    }

    try {
      const config = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ attributes })
      };

      await fetch('/cart/update.js', config);
    } catch (error) {
      console.error('Error updating cart attributes:', error);
    }
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
